// Loan-program-specific Mortgage Insurance / Fee calculations.
// Returns monthly MI, upfront fee, and metadata.

export const LOAN_TYPES = [
  { key: 'conventional', label: 'Conventional', maxLTV: 97 },
  { key: 'fha',          label: 'FHA',          maxLTV: 96.5 },
  { key: 'va',           label: 'VA',           maxLTV: 100 },
  { key: 'usda',         label: 'USDA',         maxLTV: 100 },
  { key: 'jumbo',        label: 'Jumbo',        maxLTV: 90 },
];

/**
 * Compute mortgage insurance / funding fee for a loan.
 * @param {object} p - { loanType, loanAmount, propertyPrice, termYears, firstTimeVA }
 * @returns { mi_type, monthly, upfront_fee, upfront_financed, annual_rate_pct, notes }
 */
export function computeMI({ loanType, loanAmount, propertyPrice, termYears, firstTimeVA = true }) {
  const ltv = propertyPrice > 0 ? (loanAmount / propertyPrice) * 100 : 0;

  switch (loanType) {
    case 'conventional': {
      if (ltv <= 80) {
        return { mi_type: 'None', monthly: 0, upfront_fee: 0, upfront_financed: false, annual_rate_pct: 0,
          notes: 'No PMI required at 80% LTV or below.' };
      }
      // Simplified PMI by LTV
      let rate = 0.55;
      if (ltv > 95) rate = 0.85;
      else if (ltv > 90) rate = 0.65;
      else if (ltv > 85) rate = 0.45;
      const monthly = (loanAmount * (rate / 100)) / 12;
      return { mi_type: 'PMI', monthly, upfront_fee: 0, upfront_financed: false, annual_rate_pct: rate,
        notes: `Conventional PMI at ~${rate}% annually. Auto-cancels at 78% LTV.` };
    }

    case 'fha': {
      const ufmipPct = 1.75;
      // Annual MIP (as of Mar 2023 reduction)
      let rate;
      if (termYears > 15) {
        rate = ltv > 90 ? 0.55 : 0.50;
      } else {
        rate = ltv > 90 ? 0.40 : 0.15;
      }
      const monthly = (loanAmount * (rate / 100)) / 12;
      const upfront_fee = loanAmount * (ufmipPct / 100);
      const durationNote = ltv > 90 ? 'Life of loan' : '11 years';
      return { mi_type: 'FHA MIP', monthly, upfront_fee, upfront_financed: true, annual_rate_pct: rate,
        notes: `UFMIP ${ufmipPct}% (financed). Annual MIP ${rate}% for ${durationNote}.` };
    }

    case 'va': {
      // Simplified VA funding fee — regular military (adjust for Reserves separately)
      let ffPct;
      const dpPct = propertyPrice > 0 ? ((propertyPrice - loanAmount) / propertyPrice) * 100 : 0;
      if (firstTimeVA) {
        if (dpPct >= 10) ffPct = 1.25;
        else if (dpPct >= 5) ffPct = 1.50;
        else ffPct = 2.15;
      } else {
        if (dpPct >= 10) ffPct = 1.50;
        else if (dpPct >= 5) ffPct = 1.50;
        else ffPct = 3.30;
      }
      const upfront_fee = loanAmount * (ffPct / 100);
      return { mi_type: 'VA Funding Fee', monthly: 0, upfront_fee, upfront_financed: true, annual_rate_pct: 0,
        notes: `VA funding fee ${ffPct}% (financed). No monthly MI. Exempt for disabled veterans.` };
    }

    case 'usda': {
      const ufPct = 1.00;
      const annualPct = 0.35;
      const upfront_fee = loanAmount * (ufPct / 100);
      const monthly = (loanAmount * (annualPct / 100)) / 12;
      return { mi_type: 'USDA Guarantee Fee', monthly, upfront_fee, upfront_financed: true, annual_rate_pct: annualPct,
        notes: `Upfront ${ufPct}% (financed). Annual guarantee fee ${annualPct}% (monthly).` };
    }

    case 'jumbo':
    default:
      return { mi_type: 'None', monthly: 0, upfront_fee: 0, upfront_financed: false, annual_rate_pct: 0,
        notes: 'Jumbo typically requires 20% down — no MI in most programs.' };
  }
}

export const MI_LABEL = {
  conventional: 'PMI',
  fha: 'FHA MIP',
  va: 'VA Funding Fee',
  usda: 'USDA Guarantee Fee',
  jumbo: 'MI',
};
